<template>
  <a-modal
    :open="open"
    :title="title"
    :width="640"
    :confirm-loading="submitting"
    :ok-text="okText"
    :ok-button-props="{ danger: mode === 'end', disabled: loading }"
    @ok="submit"
    @cancel="close"
  >
    <div class="end-sub">
      <p class="end-sub__client" v-if="preview">
        <strong>{{ preview.client_name || "Client" }}</strong>
        <span v-if="preview.rooms_label"> · {{ preview.rooms_label }}</span>
      </p>
      <p class="end-sub__intro">{{ intro }}</p>

      <div v-if="loading" class="end-sub__loading">Checking unpaid bills…</div>

      <template v-else-if="preview">
        <!-- 1. Why the client left -->
        <div class="end-sub__field">
          <label>Why is the client leaving? <span class="req">*</span></label>
          <a-select
            v-model:value="form.reason_category"
            placeholder="Select a reason"
            style="width: 100%"
          >
            <a-select-option
              v-for="(label, key) in preview.reasons"
              :key="key"
              :value="key"
              >{{ label }}</a-select-option
            >
          </a-select>
        </div>
        <div class="end-sub__field">
          <label>
            Details
            <span v-if="form.reason_category === 'other'" class="req">*</span>
            <span v-else class="opt">(optional)</span>
          </label>
          <a-textarea
            v-model:value="form.reason"
            :rows="2"
            placeholder="What happened, notice given, where they moved…"
          />
        </div>

        <!-- 2. What they still owe -->
        <div class="end-sub__section">
          <div class="end-sub__section-head">
            <span>Unpaid bills</span>
            <span
              class="end-sub__total"
              :class="{ 'is-clear': !preview.unpaid_total }"
            >
              {{ $comma(preview.unpaid_total) }} {{ currency }}
            </span>
          </div>
          <p v-if="!preview.unpaid_bills.length" class="end-sub__clear">
            Nothing owed — the client is leaving with all bills settled.
          </p>
          <div v-else class="end-sub__bills">
            <table>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Due</th>
                  <th class="num">Amount</th>
                  <th class="num">Paid</th>
                  <th class="num">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="b in preview.unpaid_bills" :key="b.bill_id">
                  <td>
                    {{ b.bill_number }}
                    <span
                      v-if="!b.linked"
                      class="end-sub__tag"
                      title="This bill is on the client's account but is not tied to one specific subscription"
                      >account</span
                    >
                  </td>
                  <td>{{ b.due_date || "—" }}</td>
                  <td class="num">{{ $comma(b.amount) }}</td>
                  <td class="num">{{ $comma(b.paid_amount) }}</td>
                  <td class="num strong">{{ $comma(b.balance) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 3. Management decision (only when money is owed) -->
        <template v-if="preview.unpaid_total > 0">
          <div class="end-sub__field">
            <label>
              Management decision on the unpaid amount <span class="req">*</span>
            </label>
            <a-radio-group v-model:value="form.follow_up_decision" class="end-sub__radios">
              <a-radio value="follow_up">Follow up — keep pursuing payment</a-radio>
              <a-radio value="write_off">Do not follow up (write off)</a-radio>
              <a-radio value="pending">Not decided yet</a-radio>
            </a-radio-group>
          </div>
          <div class="end-sub__field">
            <label>Decision notes <span class="opt">(optional)</span></label>
            <a-textarea
              v-model:value="form.follow_up_notes"
              :rows="2"
              placeholder="Who decided, payment plan agreed, next step…"
            />
          </div>
        </template>

        <p v-if="mode === 'end'" class="end-sub__warn">
          Ending frees the room(s) and stops future invoices. The bills above stay
          on the client's account.
        </p>
      </template>
    </div>
  </a-modal>
</template>

<script>
import { toast } from "vue3-toastify";

/**
 * The one dialog for closing out a subscription with an exit report.
 *   mode="end"      – end the subscription now (needs Subscriptions:delete)
 *   mode="request"  – no permission: raise an action request carrying the same report
 *   mode="document" – subscription already ended: add / correct its exit report
 */
export default {
  name: "EndSubscriptionModal",
  props: {
    open: { type: Boolean, default: false },
    subscription: { type: Object, default: null },
    mode: { type: String, default: "end" },
  },
  emits: ["update:open", "done"],
  data() {
    return {
      loading: false,
      submitting: false,
      preview: null,
      form: this.blankForm(),
    };
  },
  computed: {
    currency() {
      return this.$getHouse()?.currency || "";
    },
    title() {
      if (this.mode === "request") return "Request to end subscription";
      if (this.mode === "document") return "Exit report";
      return "End subscription";
    },
    okText() {
      if (this.mode === "request") return "Submit request";
      if (this.mode === "document") return "Save report";
      return "End subscription";
    },
    intro() {
      if (this.mode === "request")
        return "Fill in the exit report. Someone with permission will review it and end the subscription.";
      if (this.mode === "document")
        return "Record why this client left and what was decided about any unpaid amount.";
      return "Record why the client is leaving before the subscription is closed. This becomes the exit report kept under Past Clients.";
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) this.load();
    },
  },
  methods: {
    blankForm() {
      return {
        reason_category: undefined,
        reason: "",
        follow_up_decision: undefined,
        follow_up_notes: "",
      };
    },
    close() {
      this.$emit("update:open", false);
    },
    load() {
      this.form = this.blankForm();
      this.preview = null;
      if (!this.subscription?.subscription_id) return;
      this.loading = true;
      this.$store
        .dispatch("END_SUBSCRIPTION_PREVIEW", {
          token: this.$getAdminToken(),
          data: {
            house_id: this.$getHouseId(),
            subscription_id: this.subscription.subscription_id,
          },
        })
        .then((res) => {
          if (res?.status === 200 && res.data?.status === "success") {
            this.preview = res.data;
            // Editing an existing report: start from what was recorded.
            const r = res.data.report;
            if (r && this.mode === "document") {
              this.preview.unpaid_bills = r.unpaid_bills || [];
              this.preview.unpaid_total = Number(r.unpaid_total) || 0;
              this.form = {
                reason_category: r.reason_category,
                reason: r.reason || "",
                follow_up_decision:
                  r.follow_up_decision === "no_balance" ? undefined : r.follow_up_decision,
                follow_up_notes: r.follow_up_notes || "",
              };
            }
          } else {
            toast.error(res?.data?.message || "Could not load the unpaid bills");
            this.close();
          }
        })
        .catch(() => {
          toast.error("Could not load the unpaid bills");
          this.close();
        })
        .finally(() => (this.loading = false));
    },
    validate() {
      if (!this.form.reason_category) return "Select why the client is leaving.";
      if (this.form.reason_category === "other" && !this.form.reason.trim())
        return "Describe why the client is leaving.";
      if (this.preview.unpaid_total > 0 && !this.form.follow_up_decision)
        return "Record the management decision on the unpaid amount.";
      return null;
    },
    submit() {
      if (!this.preview || this.submitting) return;
      const problem = this.validate();
      if (problem) {
        toast.error(problem);
        return;
      }
      const report = { ...this.form, reason: this.form.reason.trim() };
      const base = {
        house_id: this.$getHouseId(),
        subscription_id: this.subscription.subscription_id,
      };
      let request;
      if (this.mode === "request") {
        request = this.$store.dispatch("CREATE_ACTION_REQUEST", {
          token: this.$getAdminToken(),
          data: {
            action_type: "end_subscription",
            entity_type: "subscription",
            entity_id: base.subscription_id,
            house_id: base.house_id,
            reason:
              (this.preview.reasons[report.reason_category] || "") +
              (report.reason ? " — " + report.reason : ""),
            payload: {
              subscription_id: base.subscription_id,
              tenant_name: this.preview.client_name,
              room_number: this.preview.rooms_label || undefined,
              unpaid_total: this.preview.unpaid_total,
              end_report: report,
            },
          },
        });
      } else {
        request = this.$store.dispatch(
          this.mode === "document" ? "SAVE_END_REPORT" : "END_SUBSCRIPTION",
          { token: this.$getAdminToken(), data: { ...base, ...report } }
        );
      }
      this.submitting = true;
      request
        .then((res) => {
          if (res?.status === 200 && res.data?.status === "success") {
            toast.success(res.data.message || "Done");
            this.$emit("done", res.data);
            this.close();
          } else {
            toast.error(res?.data?.message || "Something went wrong");
          }
        })
        .catch((err) => {
          // permission_denied 403s are already toasted by the global interceptor.
          if (err?.response?.data?.code !== "permission_denied") {
            toast.error(err?.response?.data?.message || "Something went wrong");
          }
        })
        .finally(() => (this.submitting = false));
    },
  },
};
</script>

<style lang="scss" scoped>
.end-sub {
  font-size: 0.875rem;
  color: #1f2937;

  &__client {
    margin: 0 0 0.25rem;
    font-size: 0.95rem;
  }
  &__intro {
    margin: 0 0 1rem;
    color: #6b7280;
  }
  &__loading {
    padding: 1.5rem 0;
    text-align: center;
    color: #6b7280;
  }
  &__field {
    margin-bottom: 0.9rem;
    label {
      display: block;
      margin-bottom: 0.3rem;
      font-weight: 600;
      font-size: 0.8125rem;
    }
  }
  .req {
    color: #dc2626;
  }
  .opt {
    font-weight: 400;
    color: #9ca3af;
  }
  &__section {
    margin: 1rem 0;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
  }
  &__section-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.55rem 0.8rem;
    background: #f9fafb;
    font-weight: 600;
    font-size: 0.8125rem;
  }
  &__total {
    color: #b91c1c;
    font-variant-numeric: tabular-nums;
    &.is-clear {
      color: #047857;
    }
  }
  &__clear {
    margin: 0;
    padding: 0.7rem 0.8rem;
    color: #047857;
  }
  &__bills {
    max-height: 190px;
    overflow: auto;
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.8rem;
    }
    th,
    td {
      padding: 0.4rem 0.8rem;
      text-align: left;
      border-top: 1px solid #f1f5f9;
      white-space: nowrap;
    }
    th {
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #6b7280;
      font-weight: 600;
      position: sticky;
      top: 0;
      background: #fff;
    }
    .num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    .strong {
      font-weight: 600;
    }
  }
  &__tag {
    margin-left: 0.35rem;
    padding: 0 0.35rem;
    border-radius: 4px;
    background: #f1f5f9;
    color: #64748b;
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  &__radios {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  &__warn {
    margin: 0.5rem 0 0;
    padding: 0.55rem 0.75rem;
    border-radius: 6px;
    background: #fef3c7;
    color: #92400e;
    font-size: 0.8rem;
  }
}
</style>
